import { Observable, forkJoin } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Component, OnInit, ViewChild, NgZone } from '@angular/core';

import { GridCompatibilityService } from '../services/grid-compatibility.service';
import { GridCompatibilityProcessStatus } from '../entities/enums/gridCompatibilityProcessStatus';
import { GridCompatibilityOrderProcessService } from '../services/grid-compatibility-order-process.service';
// common shared
import { FileService } from '../../services/file.service';
import { FileEvent } from '../../+shared/models/fileEvent';
import { GridConnectionApplicationType, MessageType } from '../../constants/app.constants';
import { CommonService } from '../../services/common.service';
import { SubmitService } from '../../services/submit.service';
import { ErrorService } from '../../services/error/error.service';
import { DestroyableComponent } from '../../common/destroyable.component';
import { LocalStorageService } from '../../services/local-storage.service';
import { ComponentCanDeactivate } from '../../+shared/guards/pending-changes-guard.servie';
import { Document, UpdateApplicationStatusRequest } from '../../jurisdiction/jurisdiction.domain';
// grid-application-shared
import { PrintService } from '../../+grid-application-shared/services/print.service';
import { DocumentsSectionsState } from '../../+grid-application-shared/entities/sections-state/documentsSectionsState';
import { EditabilityBasedOnProcessStatus } from '../../+grid-application-shared/entities/editabilityBasedOnProcessStatus';
import { GridApplicationDocumentTypes } from '../../+grid-application-shared/entities/enums/grid-application-document-types.enum';
import { GridApplicationProcessActionTypes } from '../../+grid-application-shared/entities/enums/grid-application-process-action-types.enum';
import { GridCompatibilityCommentsService } from '../services/grid-compatibility-comments.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { concatMap } from 'rxjs/operators';
import { FormGroup } from '@angular/forms';
import { ContactPersonService } from '../services/contact-person.service';
import { ReviewSectionHelpers } from 'src/app/+shared/components/review-section/review-section.helpers';

@Component({
  selector: 'app-grid-comp-documents-form',
  templateUrl: './grid-comp-documents-form.component.html',
  styleUrls: ['./grid-comp-documents-form.component.scss'],
})
export class GridCompDocumentsFormComponent extends DestroyableComponent implements OnInit, ComponentCanDeactivate {
  @ViewChild('documentsForm') documentsForm: any;

  private currentInstallationFlow = this.localStorageService.getItem('installation_flow');

  public isLoading: boolean = false;
  public isDetailsView: boolean = false;
  public application_id: number;
  public externalApplicationId: string;
  public requiredDocuments: Document[] = [];
  public otherDocuments: Document[] = [];
  public updateRequest: UpdateApplicationStatusRequest;
  public documentsSectionsState: DocumentsSectionsState;
  public isLockUnlockButtonClicked: boolean = false;
  public editabilityBasedOnProcessStatus: EditabilityBasedOnProcessStatus = new EditabilityBasedOnProcessStatus();
  public commentSectionOrderProcess$: Observable<any>;
  public commentSectionEditableStatuses: Array<number>;
  showAddMoreProductsButton: boolean = this.environmentService.showAddMoreProductsButton[this.currentInstallationFlow];

  private readonly requiredDocumentTypes: GridApplicationDocumentTypes[] = [GridApplicationDocumentTypes.SitePlanGenerationPlant];

  private role: string;

  public step = 4;
  public skStatus = 'submitted';
  public applicationNotFinished: boolean = false;
  public preventCanDeactivateCheck: boolean = false;
  public applicationTypeName = 'grid-comp.grid-comp-assessment';
  contactPersonForm: FormGroup;
  contactPersonData: any;
  applicationOwnerId: string;
  currentSkStatus: string;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fileService: FileService,
    private submitService: SubmitService,
    private commonService: CommonService,
    private errorService: ErrorService,
    private localStorageService: LocalStorageService,
    private zone: NgZone,
    private printService: PrintService,
    private orderProcessService: GridCompatibilityOrderProcessService,
    private gridCompatibilityService: GridCompatibilityService,
    private gridCompatibilityCommentService: GridCompatibilityCommentsService,
    private environmentService: EnvironmentService,
    private contactPersonService: ContactPersonService,
    private reviewSectionHelpers: ReviewSectionHelpers
  ) {
    super();
  }

  ngOnInit(): void {
    const urlApplcationId = Number(this.route.snapshot.paramMap.get('id'));
    const applicationIdInProgress = Number(this.localStorageService.getItem('application_id'));
    this.role = this.localStorageService.getItem('role');
    this.applicationNotFinished =
      this.localStorageService.getItem('update_application') != null || urlApplcationId === 0 || !!applicationIdInProgress;
    this.commentSectionOrderProcess$ = this.orderProcessService.orderProcess$;
    this.commentSectionEditableStatuses = [
      GridCompatibilityProcessStatus.OpenIssue,
      GridCompatibilityProcessStatus.FeedInPermissionExpired,
    ];
    if (this.environmentService.showCustomerPowerOfAttorney) {
      this.requiredDocumentTypes.push(GridApplicationDocumentTypes.CustomerPowerOfAttorney);
    }

    if (urlApplcationId || this.applicationNotFinished) {
      this.isLoading = true;
      this.application_id = urlApplcationId || applicationIdInProgress;
      this.isDetailsView = !this.applicationNotFinished;
      this.setEditabilityBasedOnProcessStatus();
      this.setDocumentsAndSectionsState();
    } else {
      this.application_id = applicationIdInProgress;
      this.updateRequest = {
        sk_status: this.skStatus,
      };
      this.requiredDocuments = [...this.requiredDocumentTypes.map(t => new Document(t))];
    }

    this.reviewSectionHelpers.initStoreData(this.application_id);
  }

  canDeactivate(): boolean | Promise<boolean> {
    return this.preventCanDeactivateCheck;
  }

  public setEditabilityBasedOnProcessStatus(): void {
    this.preventLeak(this.orderProcessService.orderProcess$).subscribe(orderProcess => {
      const gridCompProcessStatus = orderProcess.status;
      const externalRoleEditableStatuses = [GridCompatibilityProcessStatus.OpenIssue];
      const internalRoleEditableStatuses = [GridCompatibilityProcessStatus.RequestUpdated, GridCompatibilityProcessStatus.RequestSent];

      this.editabilityBasedOnProcessStatus.technicianRole = externalRoleEditableStatuses.includes(gridCompProcessStatus);
      this.editabilityBasedOnProcessStatus.customerRole = externalRoleEditableStatuses.includes(gridCompProcessStatus);
      this.editabilityBasedOnProcessStatus.internalRoles = internalRoleEditableStatuses.includes(gridCompProcessStatus);
    });
  }

  public setDocumentsAndSectionsState(): void {
    this.preventLeak(this.gridCompatibilityService.getApplication(this.application_id)).subscribe(gridCompFeedInApplication => {
      this.externalApplicationId = gridCompFeedInApplication.application_ext_id;

      this.updateRequest = {
        comment: gridCompFeedInApplication.comment,
        sk_status: this.applicationNotFinished ? this.skStatus : gridCompFeedInApplication.sk_status,
        is_operator_agreement_confirmed: gridCompFeedInApplication.is_operator_agreement_confirmed,
      };

      for (const documentType of this.requiredDocumentTypes) {
        const requiredDocument = gridCompFeedInApplication.document.find(d => d.sk_document_type === documentType);
        this.requiredDocuments.push(requiredDocument ?? new Document(documentType));
      }

      this.otherDocuments = gridCompFeedInApplication.document.filter(d => d.sk_document_type === GridApplicationDocumentTypes.Other);
      this.documentsSectionsState = gridCompFeedInApplication.documents_sections_state;
      this.contactPersonData = gridCompFeedInApplication.contact_person;
      this.applicationOwnerId = gridCompFeedInApplication.technician_id;
      this.currentSkStatus = gridCompFeedInApplication.sk_status;
      this.orderProcessService.loadOrderProcess(this.application_id);
      this.isLoading = false;
    });
  }

  private createForm(onSuccessNavigateToPannels: boolean): void {
    this.isLoading = true;

    const requests = [
      this.gridCompatibilityService.uploadDocuments(this.otherDocuments.concat(this.requiredDocuments), this.application_id.toString()),
      this.gridCompatibilityService.updateGridCompFeedInApplication(this.updateRequest, this.application_id),
    ];

    if (this.contactPersonForm) {
      requests.push(this.contactPersonService.upsert(this.contactPersonForm.value));
    }

    this.preventLeak(forkJoin(requests)).subscribe(
      ([documentsUploadResponse, applicationUpdateResponse]: [any, any?]) => {
        this.errorService.throwErrorIfFailure(documentsUploadResponse, true);
        this.errorService.throwErrorIfFailure(applicationUpdateResponse, true);
        this.submitService.setMessageText('Application created');
        this.submitService.setMessage(MessageType.REQUEST_SUBMISSION);
        this.cleanLocalStorageData();
        this.isLoading = false;
        var navigateUrl = onSuccessNavigateToPannels ? '/panel' : '/submitted';
        this.zone.run(() => {
          this.preventCanDeactivateCheck = true;
          this.router.navigate([navigateUrl]);
        });
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
        this.isLoading = false;
      }
    );
  }

  private updateForm(shouldProceed: boolean, actionType?: GridApplicationProcessActionTypes): void {
    if (this.role === 'observer') {
      return this.zone.run(() => {
        this.preventCanDeactivateCheck = true;
        this.router.navigate(['/jurisdiction-overview']);
      });
    }

    this.isLoading = true;
    const applicationId = this.application_id.toString();

    this.updateRequest.documents_sections_state = this.documentsSectionsState;

    const requests = [this.gridCompatibilityService.updateGridCompFeedInApplication(this.updateRequest, this.application_id)];

    const documentsForUpdating = this.otherDocuments.filter(d => d.file);
    if (documentsForUpdating.length) {
      requests.push(this.gridCompatibilityService.updateDocuments(documentsForUpdating, applicationId));
    }

    const requiredDocumentsForUpdating = this.requiredDocuments.filter(d => d.file);
    if (requiredDocumentsForUpdating.length) {
      requests.push(this.gridCompatibilityService.updateDocuments(requiredDocumentsForUpdating, applicationId));
    }

    if (this.contactPersonForm) {
      requests.push(this.contactPersonService.upsert(this.contactPersonForm.value));
    }

    this.preventLeak(forkJoin(requests)).subscribe(
      ([applicationUpdateResponse, documentsUpdateResponse]: [any, any?]) => {
        if (applicationUpdateResponse) {
          this.errorService.throwErrorIfFailure(applicationUpdateResponse, true);
          this.submitService.setMessageText('Application updated');
          this.submitService.setMessage(MessageType.REQUEST_SUBMISSION);
          this.isLockUnlockButtonClicked = false;
        }

        if (documentsUpdateResponse) {
          documentsForUpdating.forEach(d => (d.application_id = applicationId));
          this.errorService.throwErrorIfFailure(documentsUpdateResponse, true);
        }

        this.isLoading = false;
        this.gridCompatibilityService.application.documents_sections_state = this.documentsSectionsState;
        this.cleanLocalStorageData();

        if (shouldProceed) {
          const url = this.isModeratorRole() ? `/gridcomp/result/${applicationId}` : '/submitted';
          this.zone.run(() => {
            this.preventCanDeactivateCheck = true;
            this.router.navigate([url]);
          });
        }

        if (actionType === GridApplicationProcessActionTypes.External) {
          this.navigateToSubmittedPage();
        }

        if (actionType === GridApplicationProcessActionTypes.Internal) {
          this.navigateToJurisdictionOverview();
        }

        if (actionType === GridApplicationProcessActionTypes.AddNewProduct) {
          this.navigateToPanels();
        }
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
        this.isLoading = false;
      }
    );
  }

  initContactPersonForm(contactPersonForm: FormGroup) {
    this.contactPersonForm = contactPersonForm;
  }

  private navigateToSubmittedPage() {
    this.submitService.setMessage(MessageType.REQUEST_SUBMISSION);
    this.preventCanDeactivateCheck = true;
    this.router.navigate(['/submitted']);
  }

  private navigateToJurisdictionOverview() {
    this.commonService.showHighlightedRow = this.application_id;
    this.preventCanDeactivateCheck = true;
    this.router.navigate(['/jurisdiction-overview']);
  }

  private navigateToPanels() {
    this.router.navigate(['/panel']);
  }

  private cleanLocalStorageData() {
    this.localStorageService.removeItem('application_id');
    this.localStorageService.removeItem('update_application');
    this.localStorageService.removeItem('gridcomp_update_application');
  }

  public saveFormData(shouldProceed: boolean, isAddNewProduct: boolean): void {
    this.isDetailsView || this.applicationNotFinished
      ? this.updateForm(shouldProceed, isAddNewProduct ? GridApplicationProcessActionTypes.AddNewProduct : null)
      : this.createForm(isAddNewProduct);
  }

  public saveAndRedirectToProducts() {
    this.commonService.takeFromRequestOne = true;
    this.saveFormData(true, true);
  }

  public async updateGridCompComment(comment: string): Promise<void> {
    if (this.isModeratorRole() && this.editabilityBasedOnProcessStatus.internalRoles) {
      this.documentsSectionsState.documents_comment = comment;

      await this.saveComment();
    }
  }

  public isModeratorRole(): boolean {
    return this.role === 'employee' || this.role === 'administrator';
  }

  public clearGridCompFile(fileEvent: FileEvent): void {
    this.preventLeak(this.gridCompatibilityService.deleteDocuments(fileEvent.documentId)).subscribe(
      (response: any) => {
        this.errorService.throwErrorIfFailure(response, true);
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  public executedProcessAction(actionType: GridApplicationProcessActionTypes): void {
    this.updateForm(false, actionType);
  }

  public removeGridCompDocument(index: number): void {
    if (+this.otherDocuments[index].document_id) {
      this.clearGridCompFile(new FileEvent(+this.otherDocuments[index].document_id));
    }
    this.otherDocuments.splice(index, 1);
  }

  private async saveComment(): Promise<void> {
    if (!this.documentsSectionsState.application_id) {
      this.documentsSectionsState.application_id = this.application_id;
    }

    await this.gridCompatibilityCommentService.updateDocumentsSectionComment(this.documentsSectionsState).toPromise();
  }

  public downloadAllDocuments(): void {
    this.isLoading = true;
    this.preventLeak(
      this.printService.generatePdfByApplication(
        GridConnectionApplicationType.GridCompatibility,
        this.application_id.toString(),
        this.isModeratorRole()
      )
    )
      .pipe(
        concatMap((blob: Blob) =>
          this.fileService.getAllDocumentsByApplicationId(this.application_id.toString(), blob, 'other', 'printedForm')
        )
      )
      .subscribe(
        (response: HttpResponse<Blob>) => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(response, false);
          this.fileService.downloadZipDocument(response);
        },
        error => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
  }

  public print() {
    this.isLoading = true;

    this.preventLeak(
      this.printService.printApplication(
        GridConnectionApplicationType.GridCompatibility,
        this.application_id.toString(),
        this.isModeratorRole()
      )
    ).subscribe(() => (this.isLoading = false));
  }

  public previousPage(): void {
    this.router.navigate(['gridcomp', 'personal-data', this.application_id]);
  }

  public operatorAgreementConfirmationIsInCompliance(): boolean {
    return !this.shouldConfirmOperator || this.updateRequest.is_operator_agreement_confirmed === '1';
  }

  public requiredDocumentsFilled(): boolean {
    return !this.requiredDocuments.find(d => !d.file);
  }

  get shouldConfirmOperator(): boolean {
    return this.role === 'standard_technician' || this.role === 'guest_technician';
  }
}
